"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import type { Category } from "@/types";
import {
  MagnifyingGlassIcon,
  Squares2X2Icon,
  ClockIcon,
} from "@heroicons/react/24/outline";

type CategoryMobileHeaderProps = {
  categoryName: string;
  allCategories: Category[];
  currentSlug: string;
  pathPrefix?: string;
  pathSuffix?: string;
};

export default function CategoryMobileHeader({
  categoryName,
  allCategories,
  currentSlug,
  pathPrefix = "/categoria",
  pathSuffix = "",
}: CategoryMobileHeaderProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const router = useRouter();
  const searchParams = useSearchParams();

  const isOpenNow = searchParams.get("open_now") === "true";

  const normalizedPrefix = pathPrefix.endsWith("/")
    ? pathPrefix.slice(0, -1)
    : pathPrefix;
  const normalizedSuffix = pathSuffix
    ? pathSuffix.startsWith("/")
      ? pathSuffix
      : `/${pathSuffix}`
    : "";
  const buildPath = (slug: string) =>
    `${normalizedPrefix}/${slug}${normalizedSuffix}`;

  const toggleOpenNow = () => {
    const params = new URLSearchParams(searchParams.toString());
    if (isOpenNow) {
      params.delete("open_now");
    } else {
      params.set("open_now", "true");
    }
    params.delete("page");
    router.push(`${buildPath(currentSlug)}?${params.toString()}`);
  };

  return (
    <div className="bg-white sticky top-0 z-40 shadow-sm">
      {/* Header com voltar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
        {/* Barra de busca */}
        <div className="flex-1 relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={`Buscar em ${categoryName}`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mygreen/20"
          />
        </div>
      </div>

      {/* Tabs de categorias - scroll horizontal */}
      <div className="overflow-x-auto scrollbar-hide border-b border-gray-100">
        <div className="flex gap-2 px-4 py-3">
          {/* Botão "Todas" */}
          <Link
            href="/categorias"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors whitespace-nowrap text-sm font-medium shrink-0"
          >
            <Squares2X2Icon className="w-5 h-5" />
            <span>Todas</span>
          </Link>

          {/* Categorias */}
          {allCategories.map((cat) => (
            <Link
              key={cat.id}
              href={buildPath(cat.slug)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors whitespace-nowrap text-sm font-medium shrink-0 ${
                cat.slug === currentSlug
                  ? "bg-mygreen text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              <span>{cat.name}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Segunda linha - Filtros */}
      <div className="px-4 py-3 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2">
          {/* Botão "Aberto Agora" */}
          <button
            onClick={toggleOpenNow}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors flex gap-2 ${
              isOpenNow
                ? "bg-mygreen text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <ClockIcon className="w-4 h-4" />
            <span>Aberto Agora</span>
          </button>
        </div>
      </div>
    </div>
  );
}
